import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';

interface ProgressBarProps {
  percentage: number;
  color?: string;
  showLabel?: boolean;
  label?: string;
  height?: string;
}

const Container = styled.div`
  width: 100%;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xs};
`;

const Label = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  font-weight: ${theme.typography.weights.medium};
`;

const Percentage = styled.div`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const BarContainer = styled.div<{ height: string }>`
  width: 100%;
  height: ${props => props.height};
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  position: relative;
`;

const BarFill = styled.div<{ percentage: number; color: string }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => props.color};
  border-radius: ${theme.borderRadius.full};
  transition: width ${theme.transitions.normal}, background ${theme.transitions.normal};
  position: relative;
  overflow: hidden;

`;

const getColorByPercentage = (percentage: number): string => {
  if (percentage >= 90) return theme.colors.error;
  if (percentage >= 70) return theme.colors.warning;
  return theme.colors.success;
};

export const ProgressBar: React.FC<ProgressBarProps> = ({
  percentage,
  color,
  showLabel = true,
  label,
  height = '8px',
}) => {
  const barColor = color || getColorByPercentage(percentage);
  const clampedPercentage = Math.min(100, Math.max(0, percentage));

  return (
    <Container>
      {showLabel && (
        <LabelRow>
          {label && <Label>{label}</Label>}
          <Percentage>{clampedPercentage}%</Percentage>
        </LabelRow>
      )}
      <BarContainer height={height}>
        <BarFill percentage={clampedPercentage} color={barColor} />
      </BarContainer>
    </Container>
  );
};
